<template>
  <div v-cloak>
    <!-- Hero Section-->
    <section class="hero hero-page gray-bg padding-small">
      <div class="container">
        <div class="row d-flex">
          <div class="col-lg-9 order-2 order-lg-1">
            <h1>
              {{ styleName }}&nbsp;&nbsp;
              <img alt="icon" width="145" height="145" :src="iconImage" />
            </h1>
            <h5>{{ $t('Solids and Prints') }}</h5>
            <p class="lead text-muted">{{ $t('ChooseAnyPrint') }}</p>
            <div v-if="isAuthenticated" class="list-inline-item">
              <FavoriteButton
                :is-favorite="isFavorite"
                item-type="style"
                :item-id="styleId"
                :xsrf-token="xsrfToken"
              />
            </div>
          </div>
          <div class="col-lg-3 text-right order-1 order-lg-2">
            <ul class="breadcrumb justify-content-lg-end">
              <li class="breadcrumb-item">
                <a href="/">{{ $t('Home') }}</a>
              </li>
              <li class="breadcrumb-item">
                <a href="/styles">{{ $t('Styles') }}</a>
              </li>
              <li class="breadcrumb-item active">
                {{ styleName }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <main id="style-with-prints-list">
      <div class="container">
        <div class="row">
          <!-- Sidebar-->
          <category-filter
            :categories="categories"
            v-model="filter"
          ></category-filter>
          <!-- /Sidebar end-->
          <!-- Grid -->
          <div class="products-grid col-xl-9 col-lg-8 sidebar-left">
            <!--SOLIDS-->
            <div v-if="solidFinalPrice && solidDesigns.length > 0">
              <div class="row">
                <div class="col-sm-12">
                  <p class="text-center">
                    <span class="h3">{{ $t('Solids') }}</span>
                    <span class="h5 text-primary"
                      >&nbsp; {{ asCurrency(solidFinalPrice.amount) }}
                      <small v-if="currency !== displayCurrency">
                        {{ asDisplayCurrency(solidFinalPrice.amount) }}</small
                      ></span
                    >
                    <span
                      v-if="solidFinalPrice.amount !== solidRetailPrice.amount"
                      class="h6 text-black-50"
                      >&nbsp;<s>
                        {{ asCurrency(solidRetailPrice.amount) }}
                        <small v-if="currency !== displayCurrency"
                          ><s>
                            {{ asDisplayCurrency(solidRetailPrice.amount) }}</s
                          ></small
                        ></s
                      ></span
                    >
                  </p>
                </div>
              </div>
              <item-count
                :count="filteredSolids.length"
                :total="solidDesigns.length"
                v-model="solidItemsPerPage"
              ></item-count>
              <div class="row">
                <!-- item-->
                <div
                  class="item col-xl-3 col-md-6 col-xs-6"
                  v-for="item in filteredSolids"
                  :key="item.id"
                >
                  <style-with-print-item
                    :item="item"
                    :style-id="styleId"
                    :culture="culture"
                    :blob-domain="blobDomain"
                  ></style-with-print-item>
                </div>
              </div>
              <show-more-button v-model="solidItemsPerPage"></show-more-button>
              <item-count
                :count="filteredSolids.length"
                :total="solidDesigns.length"
                v-model="solidItemsPerPage"
              ></item-count>
            </div>

            <!--PRINTS-->
            <div v-if="repeatFinalPrice && repeatPrintDesigns.length > 0">
              <div class="row">
                <div class="col-sm-12">
                  <p class="text-center">
                    <span class="h3">{{ $t('Prints') }}</span>
                    <span class="h5 text-primary"
                      >&nbsp; {{ asCurrency(repeatFinalPrice.amount) }}
                      <small v-if="currency !== displayCurrency">
                        {{ asDisplayCurrency(repeatFinalPrice.amount) }}</small
                      ></span
                    >
                    <span
                      v-if="
                        repeatFinalPrice.amount !== repeatRetailPrice.amount
                      "
                      class="h6 text-black-50"
                      >&nbsp;<s>
                        {{ asCurrency(repeatRetailPrice.amount) }}
                        <small v-if="currency !== displayCurrency">
                          ><s>
                            {{ asDisplayCurrency(repeatRetailPrice.amount) }}</s
                          ></small
                        ></s
                      ></span
                    >
                  </p>
                </div>
              </div>
              <item-count
                :count="filteredPrints.length"
                :total="repeatPrintDesigns.length"
                v-model="printItemsPerPage"
              ></item-count>
              <div class="row">
                <!-- item-->
                <div
                  class="item col-xl-3 col-md-6 col-xs-6"
                  v-for="item in filteredPrints"
                  :key="item.id"
                >
                  <style-with-print-item
                    :item="item"
                    :style-id="styleId"
                    :culture="culture"
                    :blob-domain="blobDomain"
                  ></style-with-print-item>
                </div>
              </div>
              <show-more-button v-model="printItemsPerPage"></show-more-button>
              <item-count
                :count="filteredPrints.length"
                :total="repeatPrintDesigns.length"
                v-model="printItemsPerPage"
              ></item-count>
            </div>
          </div>
          <!-- / Grid End-->
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import type Category from '../../common/Category';
import type { CatalogItem } from '../../common/CatalogItem';
import { defineComponent, type PropType } from 'vue';
import {
  asCurrency,
  asDisplayCurrency,
  type MoneyDto,
} from '../../common/currency';
import StyleWithPrintItem from '../../components/StyleWithPrintItem.vue';
import FavoriteButton from '../../components/FavoriteButton.vue';
import CategoryFilter from '../../components/CategoryFilter.vue';
import ItemCount from '../../components/ItemCount.vue';
import ShowMoreButton from '../../components/ShowMoreButton.vue';
import { filter, intersection, isEmpty, take } from 'lodash-es';

export default defineComponent({
  name: 'StyleWithPrints',
  components: {
    FavoriteButton,
    'category-filter': CategoryFilter,
    'item-count': ItemCount,
    'show-more-button': ShowMoreButton,
    'style-with-print-item': StyleWithPrintItem,
  },
  props: {
    categories: Object as PropType<Category[]>,
    currency: String as PropType<string>,
    displayCurrency: String as PropType<string>,
    displayExchangeRate: Number as PropType<number>,
    culture: String as PropType<string>,
    blobDomain: String as PropType<string>,
    isFavorite: Boolean as PropType<boolean>,
    styleId: String as PropType<string>,
    styleName: String as PropType<string>,
    iconImage: String as PropType<string>,
    isAuthenticated: Boolean as PropType<boolean>,
    repeatPrintDesigns: Array as PropType<CatalogItem[]>,
    repeatRetailPrice: Object as PropType<MoneyDto>,
    repeatFinalPrice: Object as PropType<MoneyDto>,
    solidDesigns: Array as PropType<CatalogItem[]>,
    solidRetailPrice: Object as PropType<MoneyDto>,
    solidFinalPrice: Object as PropType<MoneyDto>,
    xsrfToken: String as PropType<string>,
  },
  data() {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = JSON.parse(urlParams.get('filter') || '{}');
    const solidItemsPerPage = Number(urlParams.get('solidItemsPerPage')) || 4;
    const printItemsPerPage = Number(urlParams.get('printItemsPerPage')) || 4;
    return {
      filterData: filter as Record<string, string[]>,
      solidItemsPerPageData: solidItemsPerPage as number,
      printItemsPerPageData: printItemsPerPage as number,
    };
  },
  methods: {
    asCurrency(amount: number): string {
      return asCurrency(this.currency, amount);
    },
    asDisplayCurrency(amount: number): string {
      return asDisplayCurrency(
        this.displayExchangeRate,
        this.displayCurrency,
        amount,
      );
    },
  },
  computed: {
    filter: {
      get() {
        return this.filterData;
      },
      set(newValue: Record<string, string[]>) {
        this.filterData = newValue;
        const url = new URL(window.location.toString());
        url.searchParams.set('filter', JSON.stringify(newValue));
        history.replaceState({}, '', url.toString());
      },
    },
    solidItemsPerPage: {
      get() {
        return this.solidItemsPerPageData;
      },
      set(newValue: number) {
        this.solidItemsPerPageData = newValue;
        const url = new URL(window.location.toString());
        url.searchParams.set('solidItemsPerPage', newValue.toString());
        history.replaceState({}, '', url.toString());
      },
    },
    printItemsPerPage: {
      get() {
        return this.printItemsPerPageData;
      },
      set(newValue: number) {
        this.printItemsPerPageData = newValue;
        const url = new URL(window.location.toString());
        url.searchParams.set('printItemsPerPage', newValue.toString());
        history.replaceState({}, '', url.toString());
      },
    },
    filteredSolids: function () {
      return take(this.solidDesigns, this.solidItemsPerPage);
    },
    filteredPrints: function () {
      if (isEmpty(this.filter)) {
        return take(this.repeatPrintDesigns, this.printItemsPerPage);
      }
      const nonEmptyCategories = filter(this.filter, function (category) {
        return category.length > 0;
      });
      //console.log(nonEmptyCategories);
      if (nonEmptyCategories.length === 0) {
        return take(this.repeatPrintDesigns, this.printItemsPerPage);
      }
      return take(
        this.repeatPrintDesigns?.filter((item) => {
          //console.log("style:");
          //console.log(style);
          return nonEmptyCategories.some((selectedChildren) => {
            //console.log("children:");
            //console.log(selectedChildren);
            if (selectedChildren.length === 0) return true;
            return intersection(item.categories, selectedChildren).length > 0;
          });
        }),
        this.printItemsPerPage,
      );
    },
  },
});
</script>
